import {
  BackLink,
  EmptyState,
  ErrorState,
  LoadingState,
  WithHalfdot,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ArticleForm } from "~/components/articles/ArticleForm";
import {
  translateCoverError,
  useCoverPublishing,
} from "~/components/covers/use-cover-publishing";
import { Screen } from "~/components/ui/Screen";
import {
  ApiError,
  createArticle,
  deleteArticle,
  fetchAdminArticle,
  updateArticle,
  uploadCover,
} from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { CoverContext } from "~/types/covers-admin";
import type { NewsArticleCreate } from "~/types/news-admin";

function translateError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return "editor.errors.slugTaken";
    if (error.status === 422) return "editor.errors.validation";
    return "common.somethingWentWrong";
  }
  return "editor.errors.network";
}

/** One editor for both modes: `/articles/new` creates, `/articles/:id` revises. */
export function EditorRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const isCreate = id === undefined;

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [savedOpen, setSavedOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const article = useQuery({
    queryKey: queryKeys.article(id ?? "new"),
    queryFn: ({ signal }) => fetchAdminArticle(id ?? "", signal),
    enabled: !isCreate,
  });

  // A save can put a library cover on (or take one off) the article, which changes
  // that cover's usage count, so the library is refreshed along with the lists.
  const invalidateLists = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.articlesRoot }),
      queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot }),
    ]);

  const create = useMutation({
    mutationFn: createArticle,
    onSuccess: async () => {
      await invalidateLists();
      void navigate("/articles");
    },
    onError: (error) => setErrorKey(translateError(error)),
  });

  const update = useMutation({
    mutationFn: (body: NewsArticleCreate) => updateArticle(id ?? "", body),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.article(updated.id), updated);
      await invalidateLists();
      setSavedOpen(true);
    },
    onError: (error) => setErrorKey(translateError(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteArticle(id ?? ""),
    onSuccess: async () => {
      // Drop the detail entry outright — invalidating it would refetch a row that no
      // longer exists and flash "not found" on the way out.
      queryClient.removeQueries({ queryKey: queryKeys.article(id ?? "") });
      await invalidateLists();
      void navigate("/articles");
    },
    onError: (error) => {
      setConfirmingDelete(false);
      setErrorKey(translateError(error));
    },
  });

  // Covers and gallery photos are only published into the library; the form holds
  // their URLs and the save commits them, so a new article gets both before it exists.
  const covers = useCoverPublishing(setErrorKey);

  // Its own mutation rather than `covers.upload`: the gallery shows its own progress,
  // and the cover field's spinner must not turn during a gallery batch.
  const uploadGalleryImage = useMutation({
    mutationFn: uploadCover,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.coversRoot }),
  });

  // The gallery shows its own progress and locks its own controls; the form's other
  // buttons still have to wait, or a save mid-batch would drop the photos after it.
  const busy =
    create.isPending ||
    update.isPending ||
    remove.isPending ||
    covers.busy ||
    uploadGalleryImage.isPending;

  const submit = (body: NewsArticleCreate) => {
    setErrorKey(null);
    if (isCreate) create.mutate(body);
    else update.mutate(body);
  };

  const handleUploadGalleryImage = async (
    file: File,
    { watermark }: { watermark: boolean },
    context: CoverContext,
  ): Promise<string | null> => {
    // No reset here: a batch calls this once per file, and the next file must not
    // wipe the reason the previous one failed.
    try {
      const published = await uploadGalleryImage.mutateAsync({
        file,
        watermark,
        context,
      });
      return published.url;
    } catch (error) {
      setErrorKey(translateCoverError(error));
      return null;
    }
  };

  const header = (
    <Box sx={{ marginBottom: 3 }}>
      <BackLink
        to="/articles"
        label={t("editor.backToList")}
        testId="editor.back"
      />
      <Typography variant="H1" sx={{ color: "text.primary", marginTop: 1 }}>
        <WithHalfdot size={11}>
          {t(isCreate ? "editor.createTitle" : "editor.editTitle")}
        </WithHalfdot>
      </Typography>
    </Box>
  );

  if (!isCreate && article.isPending) {
    return (
      <Screen maxWidth="xl">
        {header}
        <LoadingState />
      </Screen>
    );
  }

  if (!isCreate && article.isError) {
    // 422 is a URL that cannot name an article (not a UUID) — the same dead end as a
    // deleted one, and retrying would loop on it forever.
    const notFound =
      article.error instanceof ApiError &&
      (article.error.status === 404 || article.error.status === 422);
    return (
      <Screen maxWidth="xl">
        {header}
        {notFound ? (
          <EmptyState message={t("editor.notFound")} />
        ) : (
          <ErrorState onRetry={() => void article.refetch()} />
        )}
      </Screen>
    );
  }

  return (
    <Screen maxWidth="xl">
      {header}

      <ArticleForm
        // Remount when a different article — or a different *version* of it — loads.
        // Keying on updated_at means a background refetch that finds someone else's
        // newer edit resets the form to it instead of silently clobbering it on save.
        key={
          article.data ? `${article.data.id}:${article.data.updated_at}` : "new"
        }
        initial={article.data}
        busy={busy}
        errorKey={errorKey}
        submitLabel={t(isCreate ? "editor.create" : "editor.save")}
        submitTestId="editor.submit"
        onSubmit={submit}
        covers={covers}
        onUploadGalleryImage={handleUploadGalleryImage}
        actions={
          isCreate ? null : (
            <Button
              color="error"
              variant="text"
              disabled={busy}
              onClick={() => setConfirmingDelete(true)}
              data-testid="editor.delete"
            >
              {t("editor.delete")}
            </Button>
          )
        }
      />

      <Dialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      >
        <DialogTitle>{t("editor.deleteConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("editor.deleteConfirmBody", {
              title: article.data?.title ?? "",
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)} disabled={busy}>
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => remove.mutate()}
            disabled={busy}
            data-testid="editor.delete-confirm"
          >
            {t("editor.deleteConfirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={savedOpen}
        autoHideDuration={3000}
        onClose={() => setSavedOpen(false)}
        message={t("editor.saved")}
        data-testid="editor.saved"
      />
    </Screen>
  );
}
